import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../lib/apiClient';
import { getErrorMessage } from '../../lib/errors';
import type { BrandMemoryStateRead } from '../../types/api';
import { fetchBrandMemoryState, requestBrandMemoryDistill } from './brandMemoryApi';

/** How often a queued run is checked on. */
export const LEARN_POLL_INTERVAL_MS = 5_000;

/** How long "Learning…" waits for the run before handing back to the user. */
export const LEARN_POLL_TIMEOUT_MS = 120_000;

export interface BrandMemoryStateResult {
  state: BrandMemoryStateRead | null;
  isLoading: boolean;
  /** The state could not be read. The memories themselves may still be fine. */
  loadError: string | null;
  /** A run is queued and the state is being polled for it. */
  isLearning: boolean;
  /** A short sentence about the last "Learn now" that is not an error. */
  learnNotice: string | null;
  /** "Learn now" itself failed. */
  learnError: string | null;
  learn: () => Promise<void>;
  refresh: () => void;
}

interface LoadedState {
  businessId: string;
  state: BrandMemoryStateRead | null;
  error: string | null;
}

/** A run asked for on one business, and the `last_run_at` it has to move past. */
interface PendingRun {
  businessId: string;
  baseline: string | null;
}

interface LearnOutcome {
  businessId: string;
  notice: string | null;
  error: string | null;
}

/**
 * The distiller's state for one business, and the "Learn now" button over it.
 *
 * A run happens on the worker, so a click only queues it. The state is then
 * polled every 5s until `last_run_at` moves (the run finished, for better or
 * worse), for at most two minutes. `onLearned` fires when it moved, so the
 * memory list can be re-read at the one moment it can have changed.
 *
 * Everything is tagged with the business it belongs to and derived during
 * render, so switching brands mid-run never shows one brand's state on another.
 */
export function useBrandMemoryState(
  businessId: string | null,
  onLearned: () => void,
): BrandMemoryStateResult {
  const [loaded, setLoaded] = useState<LoadedState | null>(null);
  const [nonce, setNonce] = useState(0);
  const [pending, setPending] = useState<PendingRun | null>(null);
  const [outcome, setOutcome] = useState<LearnOutcome | null>(null);
  const onLearnedRef = useRef(onLearned);

  useEffect(() => {
    onLearnedRef.current = onLearned;
  }, [onLearned]);

  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!businessId) return;
    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const state = await fetchBrandMemoryState(businessId, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId, state, error: null });
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setLoaded({
          businessId,
          state: null,
          error: getErrorMessage(caught, 'Could not check what Autopostmate has learned.'),
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [businessId, nonce]);

  // Poll a queued run until `last_run_at` moves, or the two minutes are up.
  useEffect(() => {
    if (pending === null || pending.businessId !== businessId) return;

    const business = pending.businessId;
    const baseline = pending.baseline;
    const controller = new AbortController();
    const deadline = Date.now() + LEARN_POLL_TIMEOUT_MS;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    function schedule() {
      timer = setTimeout(() => {
        timer = null;
        void tick();
      }, LEARN_POLL_INTERVAL_MS);
    }

    async function tick() {
      if (cancelled) return;
      try {
        const state = await fetchBrandMemoryState(business, controller.signal);
        if (cancelled) return;
        setLoaded({ businessId: business, state, error: null });
        if (state.last_run_at !== baseline) {
          setPending(null);
          setOutcome(null);
          onLearnedRef.current();
          return;
        }
      } catch {
        // One failed check is not a failed run; keep waiting on the budget.
        if (cancelled) return;
      }

      if (Date.now() >= deadline) {
        setPending(null);
        setOutcome({
          businessId: business,
          notice: 'Still learning. Check back in a few minutes.',
          error: null,
        });
        return;
      }
      schedule();
    }

    schedule();

    return () => {
      cancelled = true;
      if (timer !== null) clearTimeout(timer);
      controller.abort();
    };
  }, [businessId, pending]);

  const isCurrent = loaded !== null && loaded.businessId === businessId;
  const state = isCurrent ? loaded.state : null;

  const learn = useCallback(async () => {
    if (!businessId) return;
    const baseline = state?.last_run_at ?? null;
    setOutcome(null);
    try {
      await requestBrandMemoryDistill(businessId);
      setPending({ businessId, baseline });
    } catch (caught) {
      if (caught instanceof ApiError && caught.code === 'NOTHING_TO_LEARN') {
        setOutcome({ businessId, notice: 'Nothing new to learn from yet.', error: null });
        refresh();
        return;
      }
      setOutcome({
        businessId,
        notice: null,
        error: getErrorMessage(caught, 'Could not start learning. Try again.'),
      });
    }
  }, [businessId, refresh, state?.last_run_at]);

  const currentOutcome = outcome !== null && outcome.businessId === businessId ? outcome : null;

  return {
    state,
    isLoading: businessId !== null && !isCurrent,
    loadError: isCurrent ? loaded.error : null,
    isLearning: pending !== null && pending.businessId === businessId,
    learnNotice: currentOutcome?.notice ?? null,
    learnError: currentOutcome?.error ?? null,
    learn,
    refresh,
  };
}
